import { SBModel } from '@supplement-bacon/react-toolkit/models'
import { PlatformUsage } from './Platform'
import { SevenroomsProfile } from './SevenroomsProfile'
import { ThirdPartyProfile } from './ThirdParty'
import { SevenroomsVenueGroup } from './venue/integrations/SevenroomsAudience'

interface User extends SBModel {
  firstName: string
  lastName: string
  email: string
  phone: string
  locale: string
  sevenroomsLoyaltyRank: number
  referralCredits: number
  canUpdateProfile: boolean
  canUpdatePicture: boolean
  whatsappPhoneId?: string
  internalNote?: string
  sevenroomsProfile?: SevenroomsProfile

  registered: PlatformUsage[]
  thirdPartyProfiles?: ThirdPartyProfile[]
  providerProfiles?: UserProviderProfile[]

  loyaltyTiers?: number[]
  businessProfile?: number
  membershipProfileId?: number
}

interface UserLegal {
  userRetentionPeriod: number // in days
  marketingOptInAt?: string
  lastInteraction: {
    date: string
  }
}

// LEGACY-ONESIGNAL - the channel a registration is on. `none` when it holds no
// identifier at all. Goes away with OneSignal.
type UserPushChannel = 'expo' | 'onesignal' | 'none'

interface UserPushChannelMetaEntry {
  label: string
  color: string
}

/** LEGACY-ONESIGNAL - channel -> Tag label/color for the subscription cards. */
const userPushChannelMeta: Record<UserPushChannel, UserPushChannelMetaEntry> = {
  expo: { label: 'Expo push', color: 'geekblue' },
  onesignal: { label: 'OneSignal', color: 'purple' },
  none: { label: 'No identifier', color: 'default' },
}

interface UserPushDeliveryVerdict {
  label: string
  color: string
  /**
   * Renders dimmed. A verdict that delivers nothing is inert, not an error, so
   * it reads muted rather than in an alarm colour -- and antd's `default`
   * alone is indistinguishable from a bare Tag.
   */
  muted?: boolean
  hint: string
}

/**
 * LEGACY-ONESIGNAL - what this registration actually does, as one verdict.
 *
 * `isServed` and `subscribed` are independent, so three of their four
 * combinations deliver nothing while each does so for a different reason. The
 * panel exists to answer "why did this person not get the notification", which
 * two orthogonal tags leave the reader to work out.
 *
 * Undefined while the API answering `isServed` is not deployed: nothing is
 * claimed rather than guessed.
 */
const userPushDeliveryVerdict = (
  registration: UserOnesignalId
): UserPushDeliveryVerdict | undefined => {
  if (registration.isServed === undefined) return undefined

  if (registration.isServed) {
    return {
      label: 'Delivers',
      color: 'green',
      hint: 'Notifications for this user go out through this registration.',
    }
  }

  if (registration.channel === 'none') {
    return {
      label: 'Does not deliver',
      color: 'default',
      muted: true,
      hint: 'This registration carries no push identifier, so it is on no channel.',
    }
  }

  if (registration.subscribed === false) {
    return {
      label: 'Does not deliver',
      color: 'default',
      muted: true,
      // The API cannot tell the two apart: retiring a device sets the same
      // flag as a user refusing notifications.
      hint: 'Notifications were refused, or the device was retired after the push service reported it unreachable.',
    }
  }

  return {
    label: 'Superseded by Expo',
    color: 'default',
    muted: true,
    hint: 'This user runs the migrated app, so their notifications go out on Expo only. This legacy registration stays listed but nothing is sent through it.',
  }
}

interface UserOnesignalId extends SBModel {
  onesignalId: string
  subscribed: boolean
  updatedAt: string
  // LEGACY-ONESIGNAL - both absent until the API answering them is deployed,
  // so every reader has to handle their absence.
  channel?: UserPushChannel
  isServed?: boolean
}

interface UserProviderProfile extends SBModel {
  providerUserId: string
  providerType: SevenroomsVenueGroup
  providerId: string
  marketingOptinAt?: string
  lastInteractionAt?: string
}

export { userPushChannelMeta, userPushDeliveryVerdict }
export type {
  User,
  UserLegal,
  UserOnesignalId,
  UserProviderProfile,
  UserPushChannel,
  UserPushDeliveryVerdict,
}
